import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  alpha,
  useTheme,
  Divider,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  AddCircleOutline as AddIcon,
  DeleteSweep as ClearIcon,
  Flag as EndIcon,
  DirectionsBoat as ShipIcon,
  History as HistoryIcon,
  CloudDone as SavedIcon,
  CloudUpload as UploadingIcon,
  Sync as SyncNowIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import TripList from "./TripList";
import TripForm from "./TripForm";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";
import { useTranslation } from "react-i18next";

const CurrentVoyage = () => {
  const { t } = useTranslation("currentvoyage");
  const theme = useTheme();

  const {
    currentVoyage: voyageData,
    isVoyageReadOnly,
    isSyncing,
    lastSynced,
    hasUnsavedChanges,
    initNewVoyage,
    saveTrip,
    amendTrip,
    completeVoyage,
    forceSync,
    fetchFromCloud,
    fetchVoyagesForShip,
    shipVoyages,
    isFetchingCloudVoyages,
    clearLocalVoyageData,
  } = useVoyageContextV2();

  const { shipData } = useShipSettings();

  // LOKALA STATES
  const [voyageNumber, setVoyageNumber] = useState("");
  const [isAddingTrip, setIsAddingTrip] = useState(false);
  const [isEditingTrip, setIsEditingTrip] = useState(false);
  const [editingTripData, setEditingTripData] = useState(null);

  const [isAmendingTrip, setIsAmendingTrip] = useState(false);
  const [amendTripId, setAmendTripId] = useState(null);
  const [amendDate, setAmendDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [amendReason, setAmendReason] = useState("");

  const [openEndVoyageDialog, setOpenEndVoyageDialog] = useState(false);
  const [openClearVoyageDialog, setOpenClearVoyageDialog] = useState(false);

  useEffect(() => {
    if (shipData?.id) {
      fetchVoyagesForShip();
    }
  }, [fetchVoyagesForShip, shipData?.id]);

  /** -------------------- HANDLERS -------------------- */

  const handleStartVoyage = () => {
    if (!voyageNumber.trim() || !shipData?.id) return;
    initNewVoyage(voyageNumber);
  };

  const handleSaveTrip = async (trip) => {
    await saveTrip(trip);
    setIsAddingTrip(false);
    setIsEditingTrip(false);
    setEditingTripData(null);
  };

  const handleEditTrip = (trip) => {
    setEditingTripData(trip);
    setIsEditingTrip(true);
  };

  const handleAmendTrip = (tripId) => {
    setAmendTripId(tripId);
    setIsAmendingTrip(true);
  };

  const handleConfirmAmendTrip = async () => {
    if (!amendDate || !amendReason.trim()) {
      alert(t("alertAmend"));
      return;
    }

    await amendTrip({
      tripId: amendTripId,
      amendDate,
      amendReason,
    });

    setIsAmendingTrip(false);
    setAmendTripId(null);
    setAmendDate(dayjs().format("YYYY-MM-DD"));
    setAmendReason("");
  };

  const handleConfirmEndVoyage = async () => {
    await completeVoyage();
    setOpenEndVoyageDialog(false);
  };

  const handleConfirmClearVoyage = () => {
    clearLocalVoyageData();
    setOpenClearVoyageDialog(false);
  };

  const hasLoadedVoyage = Boolean(voyageData);

  const formatDate = (isoDate) => {
    if (!isoDate) return "N/A";
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return parsed.toLocaleString();
  };

  const getStatusColor = (status) => {
    if (status === "active") return "success";
    if (status === "completed") return "default";
    if (status === "amended") return "warning";
    return "info";
  };

  return (
    <Box sx={{ pb: 5 }}>
      {/* === VIEW: NOT STARTED === */}
      {!hasLoadedVoyage && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mt: 8,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              width: "100%",
              maxWidth: 500,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack spacing={3}>
              <Box sx={{ textAlign: "center" }}>
                <ShipIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
                <Typography variant="h5" fontWeight="800">
                  {t("start")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "1.1rem", fontWeight: 700 }}
                >
                  {shipData?.name || t("no_ship_selected")}
                </Typography>
              </Box>

              <TextField
                label={t("voyageNumber")}
                fullWidth
                value={voyageNumber}
                onChange={(e) => setVoyageNumber(e.target.value)}
                placeholder="e.g. 2026-01"
                disabled={!shipData?.id}
              />

              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={!voyageNumber || !shipData?.id}
                onClick={handleStartVoyage}
                sx={{ py: 1.5, fontWeight: "bold", borderRadius: 2 }}
              >
                {t("startVoyage")}
              </Button>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 3,
              width: "100%",
              maxWidth: 720,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6" fontWeight={800}>
                {t("restoreActiveVoyage")}
              </Typography>
              <Button
                size="small"
                onClick={() => fetchVoyagesForShip()}
                disabled={!shipData?.id || isFetchingCloudVoyages}
              >
                {t("refresh")}
              </Button>
            </Stack>

            {isFetchingCloudVoyages ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={26} />
              </Box>
            ) : shipVoyages?.length ? (
              <Stack spacing={1.5}>
                {shipVoyages.map((voyage) => (
                  <Paper
                    key={voyage.voyageId}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        #{voyage.voyageNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("updated")}:{" "}
                        {formatDate(voyage.updatedAt || voyage.createdAt)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={voyage.status || "unknown"}
                        color={getStatusColor(voyage.status)}
                        variant="outlined"
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => fetchFromCloud(voyage.voyageId)}
                      >
                        {t("load")}
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("noCloudVoyages")}
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {/* === VIEW: VOYAGE ACTIVE === */}
      {hasLoadedVoyage && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              background: `linear-gradient(45deg, ${alpha(
                theme.palette.primary.main,
                0.05
              )}, white)`,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Typography
                  variant="overline"
                  color={isVoyageReadOnly ? "warning.main" : "primary.main"}
                  fontWeight="900"
                  sx={{ letterSpacing: 1.2 }}
                >
                  {isVoyageReadOnly ? "Read-only Voyage" : "Active Voyage"}
                </Typography>
                <Tooltip
                  title={
                    hasUnsavedChanges
                      ? "Batch Sync Pending (60s)"
                      : `Synced: ${lastSynced}`
                  }
                >
                  <Box sx={{ display: "flex" }}>
                    {isSyncing ? (
                      <CircularProgress size={14} thickness={6} />
                    ) : hasUnsavedChanges ? (
                      <UploadingIcon
                        sx={{ fontSize: 16, color: "warning.main" }}
                      />
                    ) : (
                      <SavedIcon sx={{ fontSize: 16, color: "success.main" }} />
                    )}
                  </Box>
                </Tooltip>
              </Stack>

              <Typography
                variant="h4"
                fontWeight="800"
                sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
              >
                {shipData?.name}
                <Typography
                  variant="h4"
                  component="span"
                  color="text.secondary"
                  fontWeight="300"
                >
                  #{voyageData.voyageNumber}
                </Typography>
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsAddingTrip(true)}
                disabled={isVoyageReadOnly}
                sx={{ borderRadius: 2, fontWeight: "bold" }}
              >
                {t("addTrip")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SyncNowIcon />}
                onClick={forceSync}
                disabled={isSyncing}
                sx={{ borderRadius: 2 }}
              >
                Sync now
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={() => setOpenClearVoyageDialog(true)}
                disabled={isVoyageReadOnly}
                sx={{ borderRadius: 2 }}
              >
                {t("clear")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<EndIcon />}
                onClick={() => setOpenEndVoyageDialog(true)}
                disabled={isVoyageReadOnly}
                sx={{ borderRadius: 2 }}
              >
                {t("endVoyage")}
              </Button>
            </Stack>
          </Paper>

          {/* AKTIVA TRIPS */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "800",
                color: "text.secondary",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                }}
              />
              {t("trips").toUpperCase()}
            </Typography>
            <TripList
              trips={Object.values(voyageData.trips || {})}
              onEditTrip={handleEditTrip}
              onAmendTrip={handleAmendTrip}
              readOnly={isVoyageReadOnly}
            />
          </Box>

          {/* AMENDED TRIPS (HISTORIK) */}
          {voyageData.amendedTrips &&
            Object.keys(voyageData.amendedTrips).length > 0 && (
              <Box>
                <Divider sx={{ mb: 4 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: "800",
                    color: "error.main",
                  }}
                >
                  <HistoryIcon fontSize="small" />{" "}
                  {t("amendedtrips").toUpperCase()}
                </Typography>
                <TripList
                  trips={Object.values(voyageData.amendedTrips)}
                  isAmended
                />
              </Box>
            )}
        </>
      )}

      {/* --- MODALS --- */}
      <Dialog
        open={isAddingTrip || isEditingTrip}
        onClose={() => {
          setIsAddingTrip(false);
          setIsEditingTrip(false);
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            pb: 0,
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {isAddingTrip ? t("addTrip") : t("editTrip")}
        </DialogTitle>
        <DialogContent>
          <TripForm
            tripData={isEditingTrip ? editingTripData : null}
            onSave={handleSaveTrip}
            onCancel={() => {
              setIsAddingTrip(false);
              setIsEditingTrip(false);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* AMEND DIALOG */}
      <Dialog
        open={isAmendingTrip}
        onClose={() => setIsAmendingTrip(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>{t("amendtrip")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This move the trip to history with a reference date and reason.
          </Typography>
          <Stack spacing={3}>
            <DatePicker
              label={t("amenddate")}
              value={dayjs(amendDate)}
              onChange={(v) => setAmendDate(v ? v.format("YYYY-MM-DD") : "")}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <TextField
              label={t("amendreason")}
              fullWidth
              multiline
              rows={3}
              value={amendReason}
              onChange={(e) => setAmendReason(e.target.value)}
              placeholder="e.g. Tank cleaning requirements changed..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsAmendingTrip(false)} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmAmendTrip}
            disabled={isVoyageReadOnly}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* END VOYAGE DIALOG */}
          <Dialog
            open={openClearVoyageDialog}
            onClose={() => setOpenClearVoyageDialog(false)}
          >
            <DialogTitle>{t("confirmClearVoyageTitle")}</DialogTitle>
            <DialogContent>
              <Typography>{t("confirmClearVoyageText")}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenClearVoyageDialog(false)}>
                {t("cancel")}
              </Button>
              <Button color="error" onClick={handleConfirmClearVoyage}>
                {t("confirm")}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={openEndVoyageDialog}
            onClose={() => setOpenEndVoyageDialog(false)}
          >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {t("confirmEndVoyageTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("confirmEndVoyageText")}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenEndVoyageDialog(false)}>
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmEndVoyage}
            disabled={isVoyageReadOnly}
          >
            {t("endVoyage")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CurrentVoyage;
